import * as THREE from 'three';
import type { BoatPose } from '@sea/world';

/** 鼠标视角灵敏度（弧度/像素） */
const MOUSE_SENS = 0.0022;
/** 单帧最大位移：避免 pointer lock 刚获得时那一下巨大的 movementX 把视角甩飞 */
const MAX_DELTA = 120;
/** 触摸/无指针锁时的拖动灵敏度 */
const DRAG_SENS = 0.0027;

export interface CameraRigOptions {
  /** 桌面端：点击画面锁定鼠标，直接用 movementX/Y 转视角（FPS 手感） */
  pointerLock?: boolean;
}

/**
 * 相机与视角输入。
 *
 * 两种模式：
 *  - **pointer lock（桌面默认）**：点击画面锁定鼠标，`mousemove` 的 `movementX/Y`
 *    直接转视角，鼠标不会跑出画面——这是 FPS 的标准手感，不需要按住拖动。
 *  - **drag（触摸端 / 指针锁不可用时兜底）**：按住拖动转视角。
 *
 * 相机本身不是独立节点：它由 `LocalPlayer` 挂进身体的 `headAnchor`（脖子），
 * 所以头与身体在结构上不可能不同步。
 */
export class CameraRig {
  readonly camera: THREE.PerspectiveCamera;

  private yaw = 0;
  private pitch = -0.03;

  /** 地平线锁补偿目标与当前值（弧度） */
  private targetCounterPitch = 0;
  private targetCounterRoll = 0;
  private counterPitch = 0;
  private counterRoll = 0;

  private locked = false;
  private dragging = false;
  private lastX = 0;
  private lastY = 0;
  /** 进入 WebXR 后由头显接管朝向，鼠标/拖动全部失效 */
  private xrActive = false;

  private readonly xrQ = new THREE.Quaternion();
  private readonly xrParentQ = new THREE.Quaternion();
  private readonly xrEuler = new THREE.Euler(0, 0, 0, 'YXZ');
  private readonly xrForward = new THREE.Vector3();
  private readonly xrRight = new THREE.Vector3();
  /** 最近一次有效的 yaw：视线接近垂直时投影退化，沿用上一次 */
  private lastValidYaw = 0;

  /** 锁定状态变化回调（用来切 UI 提示） */
  onLockChange: ((locked: boolean) => void) | null = null;

  constructor(
    private readonly dom: HTMLElement,
    options: CameraRigOptions = {},
  ) {
    this.camera = new THREE.PerspectiveCamera(72, 1, 0.05, 2500);
    this.camera.rotation.order = 'YXZ';
    // 眼睛在头骨前部：稍微前移，低头时才看得到胸口而不是「被胸挡住」
    this.camera.position.set(0, 0.02, 0.1);
    this.camera.rotation.set(0, 0, 0);

    const wantPointerLock = options.pointerLock ?? true;
    const pointerLockSupported = typeof dom.requestPointerLock === 'function';

    if (wantPointerLock && pointerLockSupported) {
      dom.addEventListener('click', () => {
        if (document.pointerLockElement === dom) return;
        const maybePromise = dom.requestPointerLock() as unknown as Promise<void> | undefined;
        maybePromise?.catch?.(() => {
          /* 浏览器拒绝（比如没有用户手势）时静默退回拖动模式 */
        });
      });

      document.addEventListener('pointerlockchange', () => {
        this.locked = document.pointerLockElement === dom;
        this.dragging = false;
        this.onLockChange?.(this.locked);
      });

      document.addEventListener('pointerlockerror', () => {
        this.locked = false;
        this.onLockChange?.(false);
      });

      // 锁定后：直接用鼠标位移转视角，不需要按住任何键
      document.addEventListener('mousemove', (e) => {
        if (!this.locked) return;
        this.applyDelta(e.movementX, e.movementY, MOUSE_SENS);
      });
    }

    /* ---------- 拖动兜底（触摸端与 pointer lock 不可用时）---------- */
    dom.addEventListener('pointerdown', (e) => {
      if (this.locked) return;
      this.dragging = true;
      this.lastX = e.clientX;
      this.lastY = e.clientY;
      dom.setPointerCapture?.(e.pointerId);
    });

    const endDrag = (e: PointerEvent): void => {
      if (!this.dragging) return;
      this.dragging = false;
      dom.releasePointerCapture?.(e.pointerId);
    };
    dom.addEventListener('pointerup', endDrag);
    dom.addEventListener('pointercancel', endDrag);

    dom.addEventListener('pointermove', (e) => {
      if (this.locked || !this.dragging) return;
      const dx = e.clientX - this.lastX;
      const dy = e.clientY - this.lastY;
      this.lastX = e.clientX;
      this.lastY = e.clientY;
      this.applyDelta(dx, dy, DRAG_SENS);
    });
  }

  private applyDelta(dx: number, dy: number, sens: number): void {
    if (this.xrActive) return; // 头显接管时不要再叠加鼠标视角
    const clampedX = Math.max(-MAX_DELTA, Math.min(MAX_DELTA, dx));
    const clampedY = Math.max(-MAX_DELTA, Math.min(MAX_DELTA, dy));
    this.yaw -= clampedX * sens;
    this.pitch -= clampedY * sens;
    this.pitch = Math.max(-1.15, Math.min(0.95, this.pitch));
  }

  /** 进入/退出 WebXR：进入后停用鼠标视角与地平线补偿 */
  setXRActive(active: boolean): void {
    this.xrActive = active;
    if (active) {
      this.dragging = false;
      if (document.pointerLockElement) document.exitPointerLock();
      this.onLockChange?.(false);
      this.pitch = 0;
    }
  }

  get isXRActive(): boolean {
    return this.xrActive;
  }

  /**
   * XR 下由头显姿态反推视角：把相机的世界朝向扣掉船体旋转，得到相对船体的 yaw/pitch。
   * 这样下游（身体朝向、姿态上行、眼睛偏移）全部照旧工作，不需要第二套逻辑。
   */
  syncFromXR(bodyRoot: THREE.Object3D): void {
    const cam = this.camera;
    cam.updateWorldMatrix(true, false);
    cam.getWorldQuaternion(this.xrQ);
    bodyRoot.getWorldQuaternion(this.xrParentQ).invert();
    this.xrQ.premultiply(this.xrParentQ);

    // 取朝向不能用视线向量，也不能用欧拉分解：
    //  - 欧拉分解在俯仰超过 ±90° 时会突然翻 180°；
    //  - 视线向量超过 90° 后，它的水平投影本身就是反的。
    // 用**头部右轴**：低头/弯腰时右轴始终保持水平，任何俯仰角都不会翻。
    this.xrRight.set(1, 0, 0).applyQuaternion(this.xrQ);
    const horiz = Math.hypot(this.xrRight.x, this.xrRight.z);

    if (horiz > 0.2) {
      this.yaw = Math.atan2(-this.xrRight.z, this.xrRight.x);
      this.lastValidYaw = this.yaw;
    } else {
      // 头几乎侧翻 90°：右轴退化，保持上一次的朝向
      this.yaw = this.lastValidYaw;
    }

    this.xrForward.set(0, 0, -1).applyQuaternion(this.xrQ);

    // 抬头为正
    this.pitch = Math.max(-1.4, Math.min(1.4, Math.asin(Math.max(-1, Math.min(1, this.xrForward.y)))));
  }

  get isPointerLocked(): boolean {
    return this.locked;
  }

  /**
   * 舒适度补偿：船体晃多少，就在身体/脖子上反向补掉 `(1 - motion)`。
   * motion = 0 → 画面完全平稳；motion = 1 → 完全跟着船晃。
   */
  applyComfort(pose: BoatPose, motion: number): void {
    if (this.xrActive) {
      // VR 里不能再用"反向旋转脖子"来补偿：它会和头显姿态打架，是最容易晕的做法。
      // 船体的摇晃照旧（那是世界在动），但相机不再叠加补偿旋转。
      this.targetCounterPitch = 0;
      this.targetCounterRoll = 0;
      return;
    }
    const residual = 1 - Math.min(1, Math.max(0, motion));
    this.targetCounterPitch = -pose.pitch * residual;
    this.targetCounterRoll = -pose.roll * residual;
  }

  update(dt: number): void {
    const k = 1 - Math.exp(-9 * dt);
    this.counterPitch += (this.targetCounterPitch - this.counterPitch) * k;
    this.counterRoll += (this.targetCounterRoll - this.counterRoll) * k;
  }

  resize(width: number, height: number): void {
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }

  get heading(): number {
    return this.yaw;
  }

  get lookPitch(): number {
    return this.pitch;
  }

  get comfortPitch(): number {
    return this.counterPitch;
  }

  get comfortRoll(): number {
    return this.counterRoll;
  }

  /** 调试/自检用：直接把视角转到某个朝向 */
  setYaw(yaw: number): void {
    this.yaw = yaw;
  }

  /** 调试/自检用：直接俯仰，用来验证「低头能看到身体」 */
  setPitch(pitch: number): void {
    this.pitch = Math.max(-1.15, Math.min(0.95, pitch));
  }
}
